import { useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { boPath } from "@/utils/getBasePath";
import { Toaster } from "@/components/ui/sonner";
import { useThemeStore, useAuthStore, useCartStore } from "@/store";
import { toast } from "sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { OfflineSyncProvider } from "@/providers/OfflineSyncProvider";
import { OfflineToastBridge, OfflineDebugDrawer } from "@/components/offline";
import { cleanupLegacyTokens } from "@/utils/secureApi";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// The customer storefront (not back office, admin, owner sign-up or a staff PWA)
function isStorefrontPage() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  return !(
      hostname.startsWith('app.') ||
      hostname.startsWith('admin.') ||
      hostname.startsWith('admins.') ||
      hostname.startsWith('company-admin.') ||
      hostname.startsWith('kiosk.') ||
      hostname.startsWith('pos.') ||
      hostname.startsWith('kds.') ||
      hostname.startsWith('expo.') ||
      hostname.startsWith('display.') ||
      pathname.startsWith('/back-office') ||
      pathname.startsWith('/owner') ||
      pathname.startsWith('/reset-password') ||
      pathname.startsWith('/kiosk') ||
      pathname.startsWith('/pos') ||
      pathname.startsWith('/kds') ||
      pathname.startsWith('/expo') ||
      pathname.startsWith('/display') ||
      pathname.startsWith('/dining') ||
      pathname.startsWith('/receipt/') ||
      pathname.startsWith('/merchant') ||
      pathname.startsWith('/company-admin') ||
      pathname.startsWith('/admin'));
}

// Subdomain detection for PWA routing
const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // localhost / the DigitalOcean app URL: path-based routing
  if (hostname.includes('localhost') || hostname.includes('ondigitalocean.app')) {
    return null; // Use default routing
  }
  
  // Production subdomain extraction (e.g., app.chain29.com)
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    // Only return subdomain if it's a known PWA app
    const knownSubdomains = ['pos', 'kds', 'kiosk', 'app', 'admin', 'admins', 'order', 'online', 'expo', 'display', 'timeclock'];
    if (knownSubdomains.includes(subdomain)) {
      console.log(`[Subdomain Detection] Found subdomain: ${subdomain} for hostname: ${hostname}`);
      return subdomain;
    }
    console.log(`[Subdomain Detection] Unknown subdomain: ${subdomain}`);
  }
  
  console.log('[Subdomain Detection] No valid subdomain, returning null');
  return null; // Default to main app
};

// Pages
import HomePage from "@/pages/HomePage";
import LandingPage from "@/pages/LandingPage";
import RestaurantPage from "@/pages/RestaurantPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import ProfilePage from "@/pages/ProfilePage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DriverDashboard from "@/pages/DriverDashboard";
import GroupOrderPage from "@/pages/GroupOrderPage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import FavoritesPage from "@/pages/FavoritesPage";
import AddressBookPage from "@/pages/AddressBookPage";
import ReferralPage from "@/pages/ReferralPage";
import HelpPage from "@/pages/HelpPage";
import SettingsPage from "@/pages/SettingsPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import RestaurantLandingPage from "@/pages/RestaurantLandingPage";
// the guest's receipt link (texted / e-mailed from checkout): /receipt/{signed token}
import PublicReceipt from "@/apps/dining/screens/PublicReceipt";
import MyReservationsPage from "@/pages/MyReservationsPage";

// Owner & Staff Login Pages
import { OwnerSignupPage, OwnerLoginPage, OnboardingContinue, BillingSetupPage } from "@/pages/owner";
import { StaffLoginPage, MySchedulePage, AvailabilityPage } from "@/pages/staff";

// Landing Pages (Public)
import ProductPage from "@/pages/ProductPage";
import PricingPage from "@/pages/PricingPage";
import SupportPage from "@/pages/SupportPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

// Back Office Pages (Restaurant Owner Admin)
import {
  BackOfficeLayout,
  DashboardHome as BackOfficeDashboard,
  SalesPage as BackOfficeSales,
  TransactionsPage as BackOfficeTransactions,
  OrdersPage as BackOfficeOrders,
  MenuPage as BackOfficeMenu,
  EmployeesPage as BackOfficeEmployees,
  ReportsPage as BackOfficeReports,
  InventoryPage as BackOfficeInventory,
  SettingsPage as BackOfficeSettings,
  PaymentProfilePage,
  RefundsPage as BackOfficeRefunds,
  TimesheetPage as BackOfficeTimesheet,
  PayrollPage as BackOfficePayroll,
  DiscountsPage as BackOfficeDiscounts,
  SettlementPage as BackOfficeSettlement,
  SubscriptionBlockedPage as BackOfficeBlocked,
  AppLinksPage as BackOfficeAppLinks
} from "@/pages/backoffice";
import OfflineReviewPage from "@/pages/backoffice/OfflineReviewPage";
import POSReconciliationPage from "@/pages/backoffice/POSReconciliationPage";
import IncidentAuditTrailPage from "@/pages/backoffice/IncidentAuditTrailPage";
import SupportDiagnosticsPage from "@/pages/backoffice/support-diagnostics";
import ReliabilityReportingPage from "@/pages/backoffice/reliability-reporting";
import ReliabilityAlertsPage from "@/pages/backoffice/reliability-alerts";
import UserProfilePage from "@/pages/backoffice/UserProfile";
import CampaignPage from "@/pages/backoffice/CampaignPage";
import NotificationCampaignsPage from "@/pages/backoffice/NotificationCampaignsPage";
import PaymentSettingsPage from "@/pages/backoffice/PaymentSettingsPage";
import GiftCardPage from "@/pages/backoffice/GiftCardPage";
import AsterAICommandCenter from "@/pages/AsterAICommandCenter";
import ChainAiOpsDashboard from "@/pages/ChainAiOpsDashboard";
import KioskFleetPage from "@/pages/backoffice/KioskFleetPage";
import ClientsPage from "@/pages/backoffice/ClientsPage";
import TablesReservationsPage from "@/pages/backoffice/TablesReservationsPage";
import GuestChatPage from "@/pages/backoffice/GuestChatPage";
import KioskReliabilityPage from "@/pages/backoffice/KioskReliabilityPage";

// Public Store Pages
import PublicStorefrontPage from "@/pages/public/PublicStorefrontPage";
import PublicCampaignPage from "@/pages/public/PublicCampaignPage";
import PublicGiftCardPage from "@/pages/public/PublicGiftCardPage";

// Labor Management Pages
import {
  SchedulePage as LaborSchedule,
  TipsPage as LaborTips,
  PayrollExportPage as LaborPayrollExport,
  PosLoginAccessPage,
  ApprovalCodesPage
} from "@/pages/backoffice/labor";

// Kitchen Management Pages
import {
  KitchenPrintersPage,
  KitchenStationsPage,
  KitchenRoutingPage,
  KitchenTemplatesPage,
  KitchenAnalyticsPage,
  PrintJobsPage
} from "@/pages/backoffice/kitchen";

// Order Throttle Page
import OrderThrottlePage from "@/pages/backoffice/kitchen/OrderThrottlePage";

// Menu Import
import { MenuImportPage } from "@/pages/backoffice/menu";
import MenuOverridesPage from "@/pages/backoffice/menu/MenuOverridesPage";

// Inventory Management Pages
import {
  InventoryItemsPage,
  VendorsPage,
  PurchaseOrdersPage,
  StockMovementsPage,
  ReceivingPage,
  RecipesPage,
  InventoryCountingPage,
  CycleCountPage,
  CycleCountDetailPage
} from "@/pages/backoffice/inventory";

// Billing Settings Page
import BillingSettingsPage from "@/pages/backoffice/settings/BillingPage";

// Employee Time Clock Page
import EmployeeClockPage from "@/pages/EmployeeClockPage";

// System Status Page
import StatusPage from "@/pages/StatusPage";

// Customer Facing Display (POS)
import CustomerFacingDisplay from "@/pages/CustomerFacingDisplay";

// PWA Apps for subdomain routing
import POSApp from "@/apps/pos/POSApp";
// chain29 Restaurant OS (floor, order pad, KDS, expo, checkout) - /dining/*
import DiningApp from "@/apps/dining/DiningApp";
import KDSApp from "@/apps/kds/KDSApp";
import KioskApp from "@/apps/kiosk/KioskApp";
import AdminApp from "@/apps/admin/AdminApp";
import OrderApp from "@/apps/order/OrderApp";
import ExpoApp from "@/apps/expo/ExpoApp";
import DisplayApp from "@/apps/display/DisplayApp";
import TimeclockApp from "@/apps/timeclock/TimeclockApp";

// PWA Install Prompt
import PWAInstallPrompt from "@/components/pwa/PWAInstallPrompt";

// Company Admin Pages (Super Admin)
import {
  CompanyAdminLayout,
  CompanyDashboard,
  RestaurantsPage as CompanyRestaurants,
  UsersPage as CompanyUsers,
  TransactionsPage as CompanyTransactions,
  AnalyticsPage as CompanyAnalytics,
  CompanySettingsPage,
  SubscriptionsPage as CompanySubscriptions,
  TicketsPage as CompanyTickets,
  AnnouncementsPage as CompanyAnnouncements,
  FeatureFlagsPage as CompanyFeatureFlags,
  ApiKeysPage as CompanyApiKeys,
  ReportsPage as CompanyReports,
  AuditLogsPage as CompanyAuditLogs,
  RBACPage as CompanyRBAC,
  NotificationAnalyticsPage as CompanyNotificationAnalytics,
  AutoScalingPage as CompanyAutoScaling,
  ServiceMeshPage as CompanyServiceMesh
} from "@/pages/company-admin";

// Admin Onboarding Dashboard
import { OnboardingDashboard, OnboardingDetail } from "@/pages/company-admin/onboarding";
import ContactMessagesPage from "@/pages/company-admin/support/ContactMessagesPage";


// Components
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import InstallPrompt from "@/components/InstallPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";
import AddToHomeScreen from "@/components/AddToHomeScreen";

// Import LiveOrderTracking component
import LiveOrderTracking from "@/components/tracking/LiveOrderTracking";

// Branch Context for multi-location support
import { BranchProvider } from "@/context/BranchContext";

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/auth" replace />;
}

function App() {
  const { theme, setTheme } = useThemeStore();
  const { isAuthenticated, loginWithGoogle } = useAuthStore();
  const { fetchCart } = useCartStore();
  
  // Check for subdomain-based PWA routing
  const subdomain = getSubdomain();
  console.log(`[App.js] Current subdomain: ${subdomain}, hostname: ${window.location.hostname}`);

  // Google One Tap callback
  const handleGoogleOneTap = useCallback(async (response) => {
    try {
      const result = await loginWithGoogle({ credential: response.credential });
      if (result.success) {
        toast.success('Welcome to Chain29!');
        window.location.href = '/';
      } else {
        toast.error(result.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google One Tap error:', error);
      toast.error('Google login failed');
    }
  }, [loginWithGoogle]);

  // One-time cleanup of legacy localStorage tokens
  useEffect(() => {
    cleanupLegacyTokens();
  }, []);

  useEffect(() => {
    // Initialize theme
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Initialize Google One Tap globally (shows on public pages when not authenticated)
  // Skip for admin subdomains and PWA apps (kiosk, pos, kds)
  useEffect(() => {
    // Storefront only: Google keeps one sign-in callback per page, and a
    // customer One Tap set up on the back office took the owner's Google click
    if (isAuthenticated || !GOOGLE_CLIENT_ID || !isStorefrontPage()) return;
    
    const initGoogleOneTap = () => {
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleOneTap,
            auto_select: false,
            cancel_on_tap_outside: true,
            itp_support: true,
            use_fedcm_for_prompt: true,
          });
          
          // Show One Tap prompt with FedCM support
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
              // Silently handle: browser doesn't support, user opted out, etc.
              console.debug('Google One Tap not displayed:', notification.getNotDisplayedReason());
            }
          });
        } catch (e) {
          // Silently ignore One Tap errors
          console.debug('Google One Tap init error:', e.message);
        }
      }
    };

    // Load Google script if not already loaded
    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogleOneTap;
      document.body.appendChild(script);
    } else if (window.google?.accounts?.id) {
      initGoogleOneTap();
    }
  }, [isAuthenticated, handleGoogleOneTap]);

  useEffect(() => {
    // Cart belongs to the customer storefront only — never fetch it on
    // back-office / admin / staff PWA subdomains where /api/cart is not
    // a valid concern (and the customer microservice may be unreachable,
    // producing 504s in the console).
    const isNonStorefront = !isStorefrontPage();

    if (isNonStorefront) return;

    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  // If on a PWA subdomain, render that PWA directly
  if (subdomain) {
    // For app subdomain, use the full BackOffice layout (Owner/Restaurant Back Office)
    if (subdomain === 'app') {
      return (
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <BranchProvider>
              <Routes>
                {/* Onboarding routes - public, outside BackOfficeLayout */}
                {/* ONE sign-up: the old trial pages open /owner/signup */}
                <Route path="/onboarding/restaurant/*" element={<Navigate to="/owner/signup" replace />} />
                {/* sign-up's last step (plan + billing card); the back office sends an unfinished sign-up here */}
                <Route path="/owner/signup/billing" element={<BillingSetupPage />} />
                
                {/* PWA Apps - each with their own routes */}
                <Route path="/kiosk/*" element={<KioskApp />} />
                <Route path="/pos/*" element={<POSApp />} />
                <Route path="/dining/*" element={<DiningApp />} />
                <Route path="/kds/*" element={<KDSApp />} />
                <Route path="/expo/*" element={<ExpoApp />} />
                <Route path="/display/*" element={<DisplayApp />} />
                <Route path="/order/*" element={<OrderApp />} />
                
                {/* Employee Time Clock */}
                <Route path="/time-clock" element={<EmployeeClockPage />} />
                
                {/* System Status */}
                <Route path="/system-status" element={<StatusPage />} />
                
                {/* Customer Facing Display */}
                <Route path="/cfd" element={<CustomerFacingDisplay />} />
                
                {/* Back Office routes at root — subdomain IS the back office */}
                <Route path="/*" element={<BackOfficeLayout />}>
                  <Route index element={<BackOfficeDashboard />} />
                  <Route path="dashboard" element={<Navigate to="/" replace />} />
                  <Route path="sales" element={<BackOfficeSales />} />
                  <Route path="transactions" element={<BackOfficeTransactions />} />
                  <Route path="orders" element={<BackOfficeOrders />} />
                  <Route path="menu" element={<BackOfficeMenu />} />
                  <Route path="menu/import" element={<MenuImportPage />} />
                  {/* the one AI menu builder (upload / website -> review -> publish) lives at menu/import */}
                  <Route path="ai-menu-builder/*" element={<Navigate to="../menu/import" replace />} />
                  <Route path="menu/overrides" element={<MenuOverridesPage />} />
                  <Route path="employees" element={<BackOfficeEmployees />} />
                  <Route path="reports" element={<BackOfficeReports />} />
                  <Route path="inventory" element={<BackOfficeInventory />} />
                  <Route path="settings" element={<BackOfficeSettings />} />
                  <Route path="settings/payment-profile" element={<PaymentProfilePage />} />
                  <Route path="settings/billing" element={<BillingSettingsPage />} />
                  <Route path="payment-setup" element={<PaymentSettingsPage />} />
                  <Route path="campaigns" element={<CampaignPage />} />
                  <Route path="push-campaigns" element={<NotificationCampaignsPage />} />
                  <Route path="refunds" element={<BackOfficeRefunds />} />
                  <Route path="timesheet" element={<BackOfficeTimesheet />} />
                  <Route path="payroll" element={<BackOfficePayroll />} />
                  <Route path="discounts" element={<BackOfficeDiscounts />} />
                  <Route path="settlement" element={<BackOfficeSettlement />} />
                  <Route path="billing/blocked" element={<BackOfficeBlocked />} />
                  <Route path="labor/schedule" element={<LaborSchedule />} />
                  <Route path="labor/tips" element={<LaborTips />} />
                  <Route path="pos-login-access" element={<PosLoginAccessPage />} />
                  <Route path="approval-codes" element={<ApprovalCodesPage />} />
                  <Route path="labor/payroll-export" element={<LaborPayrollExport />} />
                  <Route path="kitchen/expo" element={<Navigate to="/expo" replace />} />{/* retired page: the Expo screen is /expo (dining tickets) */}
                  <Route path="kitchen/analytics" element={<KitchenAnalyticsPage />} />
                  <Route path="kitchen/settings/printers" element={<KitchenPrintersPage />} />
                  <Route path="kitchen/settings/stations" element={<KitchenStationsPage />} />
                  <Route path="kitchen/settings/routing" element={<KitchenRoutingPage />} />
                  <Route path="kitchen/settings/templates" element={<KitchenTemplatesPage />} />
                  <Route path="kitchen/print-jobs" element={<PrintJobsPage />} />
                  <Route path="kitchen/throttle" element={<OrderThrottlePage />} />
                  <Route path="inventory/items" element={<InventoryItemsPage />} />
                  <Route path="inventory/vendors" element={<VendorsPage />} />
                  <Route path="inventory/purchase-orders" element={<PurchaseOrdersPage />} />
                  <Route path="inventory/movements" element={<StockMovementsPage />} />
                  <Route path="inventory/receiving" element={<ReceivingPage />} />
                  <Route path="inventory/recipes" element={<RecipesPage />} />
                  <Route path="inventory/counting" element={<InventoryCountingPage />} />
                  <Route path="inventory/cycle-count" element={<CycleCountPage />} />
                  <Route path="inventory/cycle-count/:taskId" element={<CycleCountDetailPage />} />
                  
                  {/* Operations Pages */}
                  <Route path="offline-review" element={<OfflineReviewPage />} />
                  <Route path="pos-reconciliation" element={<POSReconciliationPage />} />
                  <Route path="incident-audit" element={<IncidentAuditTrailPage />} />
                  <Route path="support-diagnostics" element={<SupportDiagnosticsPage />} />
                  <Route path="reliability-reporting" element={<ReliabilityReportingPage />} />
                  <Route path="reliability-alerts" element={<ReliabilityAlertsPage />} />
                  <Route path="profile" element={<UserProfilePage />} />
                  
                  <Route path="apps" element={<BackOfficeAppLinks />} />
                  <Route path="gift-cards" element={<GiftCardPage />} />
                  <Route path="ai" element={<AsterAICommandCenter />} />
                  <Route path="ai/ops" element={<ChainAiOpsDashboard />} />
                  <Route path="kiosks" element={<KioskFleetPage />} />
                  <Route path="clients" element={<ClientsPage />} />
                  <Route path="tables" element={<TablesReservationsPage />} />
                  <Route path="guest-chat" element={<GuestChatPage />} />
                  <Route path="kiosks/reliability" element={<KioskReliabilityPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </BranchProvider>
            <Toaster richColors position="top-center" />
            <PWAInstallPrompt />
          </BrowserRouter>
        </div>
      );
    }
    
    // For admin/admins subdomain, use Company Admin layout (Chain29 Super Admin portal)
    if (subdomain === 'admin' || subdomain === 'admins') {
      return (
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <Routes>
              <Route path="/*" element={<CompanyAdminLayout />}>
                <Route index element={<CompanyDashboard />} />
                <Route path="restaurants" element={<CompanyRestaurants />} />
                <Route path="subscriptions" element={<CompanySubscriptions />} />
                <Route path="users" element={<CompanyUsers />} />
                <Route path="transactions" element={<CompanyTransactions />} />
                <Route path="analytics" element={<CompanyAnalytics />} />
                <Route path="settings" element={<CompanySettingsPage />} />
                <Route path="tickets" element={<CompanyTickets />} />
                <Route path="announcements" element={<CompanyAnnouncements />} />
                <Route path="feature-flags" element={<CompanyFeatureFlags />} />
                {/* not "api-keys": on admin.chain29.com a reload of /api-keys matches the ingress's /api rule */}
                <Route path="integration-keys" element={<CompanyApiKeys />} />
                <Route path="reports" element={<CompanyReports />} />
                <Route path="audit-logs" element={<CompanyAuditLogs />} />
                <Route path="rbac" element={<CompanyRBAC />} />
                <Route path="notification-analytics" element={<CompanyNotificationAnalytics />} />
                <Route path="auto-scaling" element={<CompanyAutoScaling />} />
                <Route path="service-mesh" element={<CompanyServiceMesh />} />
                <Route path="onboarding" element={<OnboardingDashboard />} />
                <Route path="onboarding/:id" element={<OnboardingDetail />} />
                {/* boarding is done on 125cents only: the old wizard link opens the inbox */}
                <Route path="onboarding/merchant-setup" element={<Navigate to="/onboarding" replace />} />
                <Route path="support/contact-messages" element={<ContactMessagesPage />} />
              </Route>
            </Routes>
            <Toaster richColors position="top-center" />
            <PWAInstallPrompt />
          </BrowserRouter>
        </div>
      );
    }
    
    const PWAComponents = {
      'pos': POSApp,
      'kds': KDSApp,
      'kiosk': KioskApp,
      'order': OrderApp,
      'online': OrderApp, // online.chain29.com: online ordering + table reservations
      'expo': ExpoApp,
      'display': DisplayApp,
      'timeclock': TimeclockApp,
    };
    
    const PWAComponent = PWAComponents[subdomain];
    if (PWAComponent) {
      return (
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <PWAComponent />
            <Toaster richColors position="top-center" />
            <PWAInstallPrompt />
          </BrowserRouter>
        </div>
      );
    }
  }

  // Default: render main customer-facing app
  return (
    <ThemeProvider defaultTheme="dark" storageKey="asterpos-theme">
      <OfflineSyncProvider>
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
          <OfflineToastBridge />
          <Routes>
          {/* Driver dashboard has its own layout */}
          <Route path="/driver" element={<DriverDashboard />} />
          <Route path="/driver-dashboard" element={<DriverDashboard />} />
          {/* The legacy merchant dashboard is retired: the back office replaces it */}
          <Route path="/merchant" element={<Navigate to={boPath()} replace />} />
          <Route path="/merchant-dashboard" element={<Navigate to={boPath()} replace />} />
          
          {/* PWA Apps - Path-based routing for testing in preview */}
          {/* In production, these would be on separate subdomains */}
          <Route path="/pwa/pos/*" element={<POSApp />} />
          <Route path="/pwa/kds/*" element={<KDSApp />} />
          <Route path="/pwa/kiosk/*" element={<KioskApp />} />
          <Route path="/pwa/admin/*" element={<AdminApp />} />
          <Route path="/pwa/order/*" element={<OrderApp />} />
          <Route path="/pwa/expo/*" element={<ExpoApp />} />
          <Route path="/pwa/display/*" element={<DisplayApp />} />
          <Route path="/pwa/timeclock/*" element={<TimeclockApp />} />
          
          {/* Back Office - Clover-style admin dashboard */}
          <Route path="/back-office" element={
            <BranchProvider>
              <BackOfficeLayout />
            </BranchProvider>
          }>
            <Route index element={<BackOfficeDashboard />} />
            <Route path="dashboard" element={<Navigate to="/back-office" replace />} />
            <Route path="sales" element={<BackOfficeSales />} />
            <Route path="transactions" element={<BackOfficeTransactions />} />
            <Route path="orders" element={<BackOfficeOrders />} />
            <Route path="menu" element={<BackOfficeMenu />} />
            <Route path="menu/import" element={<MenuImportPage />} />
            {/* the one AI menu builder (upload / website -> review -> publish) lives at menu/import */}
            <Route path="ai-menu-builder/*" element={<Navigate to="../menu/import" replace />} />
            <Route path="menu/overrides" element={<MenuOverridesPage />} />
            <Route path="employees" element={<BackOfficeEmployees />} />
            <Route path="reports" element={<BackOfficeReports />} />
            <Route path="inventory" element={<BackOfficeInventory />} />
            <Route path="settings" element={<BackOfficeSettings />} />
            <Route path="settings/payment-profile" element={<PaymentProfilePage />} />
            <Route path="settings/billing" element={<BillingSettingsPage />} />
            <Route path="refunds" element={<BackOfficeRefunds />} />
            <Route path="timesheet" element={<BackOfficeTimesheet />} />
            <Route path="payroll" element={<BackOfficePayroll />} />
            <Route path="discounts" element={<BackOfficeDiscounts />} />
            <Route path="settlement" element={<BackOfficeSettlement />} />
            {/* Subscription/Billing */}
            <Route path="billing/blocked" element={<BackOfficeBlocked />} />
            {/* Labor Management Routes */}
            <Route path="labor/schedule" element={<LaborSchedule />} />
            <Route path="labor/tips" element={<LaborTips />} />
            <Route path="pos-login-access" element={<PosLoginAccessPage />} />
                  <Route path="approval-codes" element={<ApprovalCodesPage />} />
            <Route path="labor/payroll-export" element={<LaborPayrollExport />} />
            {/* Kitchen Management Routes */}
            <Route path="kitchen/expo" element={<Navigate to="/expo" replace />} />{/* retired page: the Expo screen is /expo (dining tickets) */}
            <Route path="kitchen/analytics" element={<KitchenAnalyticsPage />} />
            <Route path="kitchen/settings/printers" element={<KitchenPrintersPage />} />
            <Route path="kitchen/settings/stations" element={<KitchenStationsPage />} />
            <Route path="kitchen/settings/routing" element={<KitchenRoutingPage />} />
            <Route path="kitchen/settings/templates" element={<KitchenTemplatesPage />} />
            <Route path="kitchen/print-jobs" element={<PrintJobsPage />} />
            <Route path="kitchen/throttle" element={<OrderThrottlePage />} />
            {/* Inventory Management Routes */}
            <Route path="inventory/items" element={<InventoryItemsPage />} />
            <Route path="inventory/vendors" element={<VendorsPage />} />
            <Route path="inventory/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="inventory/movements" element={<StockMovementsPage />} />
            <Route path="inventory/receiving" element={<ReceivingPage />} />
            <Route path="inventory/recipes" element={<RecipesPage />} />
            <Route path="inventory/counting" element={<InventoryCountingPage />} />
            <Route path="inventory/cycle-count" element={<CycleCountPage />} />
            <Route path="inventory/cycle-count/:taskId" element={<CycleCountDetailPage />} />
            {/* App Links */}
            <Route path="apps" element={<BackOfficeAppLinks />} />
            {/* Offline Sync Review - Manager Queue */}
            <Route path="offline-review" element={<OfflineReviewPage />} />
            {/* POS Reconciliation Dashboard */}
            <Route path="pos-reconciliation" element={<POSReconciliationPage />} />
            {/* Incident Audit Trail */}
            <Route path="incident-audit" element={<IncidentAuditTrailPage />} />
            {/* Support Diagnostics - Phase 14 */}
            <Route path="support-diagnostics" element={<SupportDiagnosticsPage />} />
            {/* Reliability Reporting - Phase 14 */}
            <Route path="reliability-reporting" element={<ReliabilityReportingPage />} />
            {/* Reliability Alerts - Phase 15 */}
            <Route path="reliability-alerts" element={<ReliabilityAlertsPage />} />
            {/* User Profile */}
            <Route path="profile" element={<UserProfilePage />} />
            {/* Campaigns & Gift Cards */}
            <Route path="campaigns" element={<CampaignPage />} />
            <Route path="push-campaigns" element={<NotificationCampaignsPage />} />
            <Route path="gift-cards" element={<GiftCardPage />} />
            <Route path="payment-setup" element={<PaymentSettingsPage />} />
            <Route path="ai" element={<AsterAICommandCenter />} />
            <Route path="ai/ops" element={<ChainAiOpsDashboard />} />
            <Route path="kiosks" element={<KioskFleetPage />} />
                  <Route path="clients" element={<ClientsPage />} />
                  <Route path="tables" element={<TablesReservationsPage />} />
                  <Route path="guest-chat" element={<GuestChatPage />} />
            <Route path="kiosks/reliability" element={<KioskReliabilityPage />} />
            <Route path="*" element={<Navigate to="/back-office" replace />} />
          </Route>
          
          {/* Employee Time Clock - Standalone page */}
          <Route path="/time-clock" element={<EmployeeClockPage />} />
          
          {/* System Status Page - Public */}
          <Route path="/system-status" element={<StatusPage />} />
          
          {/* Customer Facing Display - For POS touch screen */}
          <Route path="/cfd" element={<CustomerFacingDisplay />} />
          
          {/* Company Admin - Super Admin portal */}
          <Route path="/company-admin" element={<CompanyAdminLayout />}>
            <Route index element={<CompanyDashboard />} />
            <Route path="restaurants" element={<CompanyRestaurants />} />
            <Route path="users" element={<CompanyUsers />} />
            <Route path="tickets" element={<CompanyTickets />} />
            <Route path="announcements" element={<CompanyAnnouncements />} />
            <Route path="feature-flags" element={<CompanyFeatureFlags />} />
            <Route path="settings" element={<CompanySettingsPage />} />
            <Route path="integration-keys" element={<CompanyApiKeys />} />
            <Route path="api-keys" element={<Navigate to="/company-admin/integration-keys" replace />} />
            <Route path="subscriptions" element={<CompanySubscriptions />} />
            <Route path="reports" element={<CompanyReports />} />
            <Route path="audit-logs" element={<CompanyAuditLogs />} />
            <Route path="rbac" element={<CompanyRBAC />} />
            <Route path="transactions" element={<CompanyTransactions />} />
            <Route path="analytics" element={<CompanyAnalytics />} />
            <Route path="notification-analytics" element={<CompanyNotificationAnalytics />} />
            <Route path="auto-scaling" element={<CompanyAutoScaling />} />
            <Route path="service-mesh" element={<CompanyServiceMesh />} />
            {/* Onboarding Inbox */}
            <Route path="onboarding" element={<OnboardingDashboard />} />
            <Route path="onboarding/:id" element={<OnboardingDetail />} />
            {/* boarding is done on 125cents only: the old wizard links open the inbox */}
            <Route path="onboarding/merchant-setup" element={<Navigate to="/company-admin/onboarding" replace />} />
            <Route path="onboarding/payment-hub-boarding" element={<Navigate to="/company-admin/onboarding" replace />} />
            {/* Support Inbox */}
            <Route path="support/contact-messages" element={<ContactMessagesPage />} />
          </Route>
          
          {/* Restaurant Onboarding - Public, no navbar */}
          {/* ONE sign-up: the old trial pages open /owner/signup */}
          <Route path="/onboarding/restaurant/*" element={<Navigate to="/owner/signup" replace />} />
          
          {/* PWA Apps - Direct path access for all environments */}
          <Route path="/pos/*" element={<POSApp />} />
          {/* chain29 Restaurant OS: floor, order pad, KDS, expo, checkout (staff PIN sign-in) */}
          <Route path="/dining/*" element={<DiningApp />} />
          <Route path="/kiosk/*" element={<KioskApp />} />
          <Route path="/kds/*" element={<KDSApp />} />
          <Route path="/expo/*" element={<ExpoApp />} />
          <Route path="/display/*" element={<DisplayApp />} />
          <Route path="/order/*" element={<OrderApp />} />
          <Route path="/admin/*" element={<AdminApp />} />
          
          {/* the old /kitchen screen read a token nothing stores (always 401): the kitchen display is /kds */}
          <Route path="/kitchen" element={<Navigate to="/kds" replace />} />
          
          {/* Public Gift Card Storefront - No navbar */}
          <Route path="/store/:slug" element={<PublicStorefrontPage />} />
          <Route path="/store/:slug/gift-cards" element={<PublicGiftCardPage />} />
          <Route path="/store/:slug/:campaignSlug" element={<PublicCampaignPage />} />
          
          {/* Restaurant Landing Pages - Public, no navbar */}
          <Route path="/r/:slug" element={<RestaurantLandingPage />} />
          {/* A guest's receipt (signed, expiring link) - public, no navbar */}
          <Route path="/receipt/:token" element={<PublicReceipt />} />
          
          {/* Live Order Tracking - Full screen, no navbar */}
          <Route path="/live-track/:id" element={<LiveOrderTracking />} />
          
          {/* Onboarding - Full screen, no navbar */}
          <Route path="/restaurant-onboarding" element={<Navigate to="/owner/signup" replace />} />
          <Route path="/onboarding" element={<Navigate to="/restaurant-onboarding" replace />} />
          <Route path="/get-started" element={<Navigate to="/restaurant-onboarding" replace />} />
          
          {/* Owner Routes - Full screen, no navbar */}
          <Route path="/owner/signup" element={<OwnerSignupPage />} />
          <Route path="/owner/signup/continue" element={<OnboardingContinue />} />
          <Route path="/owner/signup/billing" element={<BillingSetupPage />} />
          <Route path="/owner/login" element={<OwnerLoginPage />} />
          
          {/* Staff Routes */}
          <Route path="/staff/login" element={<StaffLoginPage />} />
          <Route path="/staff/schedule" element={<MySchedulePage />} />
          <Route path="/staff/availability" element={<AvailabilityPage />} />
          
          {/* Customer Login Redirect (backward compat) */}
          <Route path="/customer/login" element={<AuthPage />} />
          {/* the password reset email links here (routes/password_reset) */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Landing Page - Owner-first homepage (default) */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Public Marketing Pages */}
          <Route path="/product" element={<ProductPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Customer pages (restaurants, cart, checkout, orders, profile) - with
              Navbar. They link to root paths (/checkout, /orders, /restaurant/:id);
              mounted at /order/* they were never reachable (/order/* is the
              online-order app). Root, lowest rank: every route above wins. */}
          <Route path="/*" element={
            <>
              <Navbar />
              <CartDrawer />
              <MobileBottomNav />
              <main className="pt-16">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/restaurants" element={<HomePage />} />
                  <Route path="/restaurant/:id" element={<RestaurantPage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/checkout" element={
                    <ProtectedRoute><CheckoutPage /></ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute><OrdersPage /></ProtectedRoute>
                  } />
                  <Route path="/orders/:id" element={
                    <ProtectedRoute><OrderDetailPage /></ProtectedRoute>
                  } />
                  <Route path="/reservations" element={
                    <ProtectedRoute><MyReservationsPage /></ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute><ProfilePage /></ProtectedRoute>
                  } />
                  <Route path="/group-order" element={
                    <ProtectedRoute><GroupOrderPage /></ProtectedRoute>
                  } />
                  <Route path="/group-order/:code" element={
                    <ProtectedRoute><GroupOrderPage /></ProtectedRoute>
                  } />
                  <Route path="/loyalty" element={
                    <ProtectedRoute><LoyaltyPage /></ProtectedRoute>
                  } />
                  <Route path="/favorites" element={
                    <ProtectedRoute><FavoritesPage /></ProtectedRoute>
                  } />
                  <Route path="/addresses" element={
                    <ProtectedRoute><AddressBookPage /></ProtectedRoute>
                  } />
                  <Route path="/referral" element={
                    <ProtectedRoute><ReferralPage /></ProtectedRoute>
                  } />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/settings" element={
                    <ProtectedRoute><SettingsPage /></ProtectedRoute>
                  } />
                  <Route path="/track/:id" element={
                    <ProtectedRoute><OrderTrackingPage /></ProtectedRoute>
                  } />
                  {/* Unknown paths: never render an empty page */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </>
          } />
          
        </Routes>
        <Toaster richColors position="top-center" />
        <OfflineIndicator />
        <OfflineDebugDrawer />
        <InstallPrompt />
        <AddToHomeScreen />
      </BrowserRouter>
    </div>
      </OfflineSyncProvider>
    </ThemeProvider>
  );
}

export default App;
